import type { AxiosError } from 'axios';

export type AppErrorCode =
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'VALIDATION'
  | 'CONFLICT'
  | 'RATE_LIMIT'
  | 'SERVER'
  | 'NETWORK'
  | 'UNKNOWN';

export class AppError extends Error {
  status?: number;
  code: AppErrorCode;
  details?: unknown;
  raw?: unknown;

  constructor(opts: {
    message: string;
    status?: number;
    code?: AppErrorCode;
    details?: unknown;
    raw?: unknown;
  }) {
    super(opts.message);
    this.name = 'AppError';
    this.status = opts.status;
    this.code = opts.code ?? 'UNKNOWN';
    this.details = opts.details;
    this.raw = opts.raw;
  }
}

export function normalizeAxiosError(err: unknown): AppError {
  const ax = err as AxiosError<any>;
  if (!ax || !ax.isAxiosError) {
    return new AppError({
      message: (err as Error)?.message ?? 'Unknown error',
      code: 'UNKNOWN',
      raw: err,
    });
  }

  if (ax.code === 'ERR_NETWORK') {
    return new AppError({ message: 'Network error', code: 'NETWORK', raw: err });
  }

  const status = ax.response?.status;
  const payload = ax.response?.data;
  const message = payload?.message || ax.message || 'Unknown error';

  const statusToCode = (s?: number): AppErrorCode => {
    if (s === 401) return 'UNAUTHORIZED';
    if (s === 403) return 'FORBIDDEN';
    if (s === 404) return 'NOT_FOUND';
    if (s === 409) return 'CONFLICT';
    if (s === 422) return 'VALIDATION';
    if (s === 429) return 'RATE_LIMIT';
    if (!s || s >= 500) return 'SERVER';
    return 'UNKNOWN';
  };

  return new AppError({
    message,
    status,
    code: payload?.code ?? statusToCode(status),
    details: payload?.errors || payload?.details,
    raw: err,
  });
}
